import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiClientError } from './client';

/** Website chat widget settings (ADR-038). */
export interface WidgetSettings {
  title: string;
  greeting: string;
  color: string;
  position: 'right' | 'left';
  preChat: { enabled: boolean; askName: boolean; askPhone: boolean; askEmail: boolean };
}

export interface WebsiteChannel {
  channelId: string;
  name: string;
  status: string;
  /** Public site key used in the install snippet. */
  siteKey: string;
  allowedOrigins: string[];
  settings: WidgetSettings;
  starterAutomationId: string | null;
  createdAt: string;
}

export interface CreateWebsiteChannelInput {
  name: string;
  allowedOrigins: string[];
  settings?: Partial<WidgetSettings>;
  starterAutomation?: boolean;
}

export interface UpdateWebsiteChannelInput {
  name?: string;
  allowedOrigins?: string[];
  settings?: WidgetSettings;
}

const KEY = ['website-channels'] as const;

export function useWebsiteChannels(enabled = true) {
  return useQuery<WebsiteChannel[], ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<WebsiteChannel[]>('/channels/website'),
    enabled,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ['channels'] });
  };
}

export function useCreateWebsiteChannel() {
  const invalidate = useInvalidate();
  return useMutation<WebsiteChannel, ApiClientError, CreateWebsiteChannelInput>({
    mutationFn: (input) => apiClient.post<WebsiteChannel>('/channels/website', input),
    onSettled: invalidate,
  });
}

export function useUpdateWebsiteChannel(channelId: string) {
  const invalidate = useInvalidate();
  return useMutation<WebsiteChannel, ApiClientError, UpdateWebsiteChannelInput>({
    mutationFn: (input) => apiClient.patch<WebsiteChannel>(`/channels/website/${channelId}`, input),
    onSettled: invalidate,
  });
}

export function useRotateSiteKey(channelId: string) {
  const invalidate = useInvalidate();
  return useMutation<WebsiteChannel, ApiClientError, void>({
    mutationFn: () => apiClient.post<WebsiteChannel>(`/channels/website/${channelId}/rotate-key`),
    onSettled: invalidate,
  });
}

export function useRemoveWebsiteChannel(channelId: string) {
  const invalidate = useInvalidate();
  return useMutation<void, ApiClientError, void>({
    mutationFn: () => apiClient.delete<void>(`/channels/website/${channelId}`),
    onSettled: invalidate,
  });
}

/** The snippet a site owner pastes before </body>. */
export function installSnippet(siteKey: string, appOrigin = window.location.origin): string {
  return `<script src="${appOrigin}/widget.js" data-key="${siteKey}" async></script>`;
}
